import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PaymentService } from '../payment.service';
import { PaymentDetails, Settlement } from '../payment.type';
import { PaymentSourceFields } from '../payment-source-fields/payment-source-fields';

export interface SettlementPaymentDialogData {
  settlement: Settlement;
}

/**
 * Records or corrects how a paid settlement was paid — for one paid before
 * accounts were recorded, or to fix a wrong pick. Closes with true on save.
 */
@Component({
  selector: 'app-settlement-payment-dialog',
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule, PaymentSourceFields],
  template: `
    <h2 mat-dialog-title>How {{ s.supplier_name }} was paid</h2>
    <mat-dialog-content>
      <p class="hint">{{ s.amount | number }} Toman · paid {{ s.paid_at | date:'medium' }}</p>
      <app-payment-source-fields [details]="payment" [disabled]="saving"></app-payment-source-fields>
      @if (error) { <p class="error">{{ error }}</p> }
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button type="button" [disabled]="saving" (click)="close(false)">Cancel</button>
      <button mat-flat-button type="button" color="primary" [disabled]="saving || !payment.source_account_id" (click)="save()">
        @if (saving) { <mat-spinner diameter="18"></mat-spinner> } @else { <mat-icon>save</mat-icon> }
        Save
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    .hint { margin: 0 0 8px; opacity: .75; }
    .error { color: var(--mat-sys-error, #c62828); }
  `],
})
export class SettlementPaymentDialog {
  private dialogRef = inject(MatDialogRef<SettlementPaymentDialog, boolean>);
  private paymentService = inject(PaymentService);
  private snackBar = inject(MatSnackBar);
  readonly s = inject<SettlementPaymentDialogData>(MAT_DIALOG_DATA).settlement;

  payment: PaymentDetails = {
    source_account_id: this.s.source_account_id ?? '',
    bank_fee: this.s.bank_fee ?? 0,
    transaction_ref: this.s.transaction_ref ?? '',
  };
  saving = false;
  error = '';

  save(): void {
    if (!this.payment.source_account_id || this.saving) return;
    this.saving = true;
    this.error = '';
    this.paymentService.updateSettlementPayment(this.s.id, this.payment).subscribe({
      next: () => {
        this.snackBar.open('Saved', 'Close', { duration: 2500 });
        this.close(true);
      },
      error: err => {
        this.saving = false;
        this.error = err?.error?.error ?? 'Failed to save';
      },
    });
  }

  close(changed: boolean): void {
    this.dialogRef.close(changed);
  }
}
